import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    InputAdornment,
    useTheme,
    CircularProgress,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SearchIcon from '@material-ui/icons/Search'
import {
    EnhancedTableHead,
    HeadCell,
    LoadingButton,
    Order,
    RowData,
    CellData,
    Selector,
    stableSort,
} from '../../../components'
import { useStyles } from '../../../styles'
import { useOutreach } from '../../../hooks/Outreach/useOutreach'
import { AdminSchoolRow } from '../../../models/Outreach/types'
import { typographyStyle } from '../../../theme/typographyStyle'
import { ProspectsTableFooter } from '../Outreach/ProspectsTableFooter'
import { GLOBAL_TOP_BAR_HEIGHT } from '../../../constants'

const CATALOG_HELP_PARAGRAPHS = [
    'The effective name is what target search sends to Crust: EZOS override, then Google Places, then the IPEDS record name.',
    'Outreach eligible schools can be suggested as schools a property serves.',
    'A school is outreach eligible when College Scorecard fall enrollment is at least 2,500 and the highest degree awarded is Associate, Bachelor, or Graduate. Certificate-only schools and schools missing enrollment or degree data are not eligible.',
]

type CatalogFilterOption = { id: number; name: string }

const YES_NO_FILTER: CatalogFilterOption[] = [
    { id: 1, name: 'Yes' },
    { id: 0, name: 'No' },
]

const PLACES_NAME_FILTER: CatalogFilterOption[] = [
    { id: 1, name: 'Present' },
    { id: 0, name: 'Missing' },
]

const OVERRIDE_FILTER: CatalogFilterOption[] = [
    { id: 1, name: 'Set' },
    { id: 0, name: 'Empty' },
]

const FILTER_ANY = -1
const FILTER_YES = 1
const FILTER_NO = 0

function hasText(value: string | undefined | null): boolean {
    return Boolean(value?.trim())
}

function schoolMatchesFilters(
    school: AdminSchoolRow,
    eligibleFilter: number,
    placesFilter: number,
    overrideFilter: number,
): boolean {
    if (eligibleFilter === FILTER_YES && !school.outreach_eligible) return false
    if (eligibleFilter === FILTER_NO && school.outreach_eligible) return false
    const hasPlacesName = hasText(school.google_places_display_name)
    if (placesFilter === FILTER_YES && !hasPlacesName) return false
    if (placesFilter === FILTER_NO && hasPlacesName) return false
    const hasOverride = hasText(school.ezos_admin_display_name_override)
    if (overrideFilter === FILTER_YES && !hasOverride) return false
    if (overrideFilter === FILTER_NO && hasOverride) return false
    return true
}

interface SchoolsTableRow extends RowData {
    schoolId: CellData<number>
    recordName: CellData<string>
    effectiveName: CellData<string>
    placesName: CellData<string>
    overrideDisplay: CellData<string>
    outreachEligible: CellData<string>
    schoolRef: CellData<AdminSchoolRow>
}

function descendingComparator<T extends RowData>(
    a: T,
    b: T,
    orderBy: keyof T,
): number {
    if (b[orderBy].sortValue < a[orderBy].sortValue) {
        return -1
    }
    if (b[orderBy].sortValue > a[orderBy].sortValue) {
        return 1
    }
    return 0
}

function getComparator<T extends RowData>(
    order: Order,
    orderBy: keyof T,
): (a: T, b: T) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy)
}

function displayOrDash(value: string | undefined | null): string {
    const t = value?.trim()
    return t ? t : '—'
}

function yesNo(value: boolean): string {
    return value ? 'Yes' : 'No'
}

function normalizeSearch(text: string): string {
    return text.trim().toLowerCase()
}

function schoolMatchesSearch(
    school: AdminSchoolRow,
    queryRaw: string,
): boolean {
    const q = normalizeSearch(queryRaw)
    if (!q) return true
    const haystacks = [
        school.name,
        school.effective_display_name,
        school.google_places_display_name ?? '',
        school.ezos_admin_display_name_override ?? '',
        school.ipeds_id ?? '',
    ]
    return haystacks.some((h) => normalizeSearch(h).includes(q))
}

function createSchoolsTableRow(school: AdminSchoolRow): SchoolsTableRow {
    const override = school.ezos_admin_display_name_override
    return {
        schoolId: {
            value: school.id,
            sortValue: school.id,
        },
        recordName: {
            value: school.name,
            sortValue: school.name.toLowerCase(),
        },
        effectiveName: {
            value: school.effective_display_name,
            sortValue: school.effective_display_name.toLowerCase(),
        },
        placesName: {
            value: displayOrDash(school.google_places_display_name),
            sortValue: (school.google_places_display_name || '').toLowerCase(),
        },
        overrideDisplay: {
            value: displayOrDash(override),
            sortValue: (override || '').toLowerCase(),
        },
        outreachEligible: {
            value: yesNo(school.outreach_eligible),
            sortValue: school.outreach_eligible ? 1 : 0,
        },
        schoolRef: {
            value: school,
            sortValue: school.effective_display_name.toLowerCase(),
        },
    }
}

interface SchoolEzosOverrideDialogProps {
    open: boolean
    school: AdminSchoolRow | null
    onClose: () => void
    onSave: (ezos_admin_display_name_override: string) => Promise<void>
}

const SchoolEzosOverrideDialog: React.FC<SchoolEzosOverrideDialogProps> = ({
    open,
    school,
    onClose,
    onSave,
}) => {
    const [draft, setDraft] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (open && school) {
            setDraft(school.ezos_admin_display_name_override ?? '')
        }
    }, [open, school])

    const handleSave = async () => {
        setSaving(true)
        try {
            await onSave(draft.trim())
            onClose()
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                EZOS display name override
                {school ? ` — ${school.effective_display_name}` : ''}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="EZOS admin display name override"
                    helperText="Leave empty to clear the override. Target search uses this name, then the Google Places name, then the IPEDS record name."
                    fullWidth
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    disabled={saving}
                />
            </DialogContent>
            <DialogActions>
                <LoadingButton onClick={onClose} disabled={saving}>
                    Cancel
                </LoadingButton>
                <LoadingButton
                    color="primary"
                    variant="contained"
                    loading={saving}
                    onClick={handleSave}
                >
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export const AdminSchoolsList: React.FC = () => {
    const theme = useTheme()
    const classes = useStyles()
    const {
        adminSchools,
        isLoadingAdminSchools,
        updateAdminSchoolOverride,
    } = useOutreach({ loadAdminSchools: true })

    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<keyof SchoolsTableRow>(
        'effectiveName',
    )
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [dialogSchool, setDialogSchool] = useState<AdminSchoolRow | null>(
        null,
    )
    const [helpOpen, setHelpOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [eligibleFilter, setEligibleFilter] = useState(FILTER_YES)
    const [placesFilter, setPlacesFilter] = useState(FILTER_ANY)
    const [overrideFilter, setOverrideFilter] = useState(FILTER_ANY)

    const filteredSchools = useMemo(
        () =>
            (adminSchools ?? []).filter(
                (school) =>
                    schoolMatchesFilters(
                        school,
                        eligibleFilter,
                        placesFilter,
                        overrideFilter,
                    ) && schoolMatchesSearch(school, searchQuery),
            ),
        [
            adminSchools,
            searchQuery,
            eligibleFilter,
            placesFilter,
            overrideFilter,
        ],
    )

    useEffect(() => {
        setPage(0)
    }, [searchQuery, eligibleFilter, placesFilter, overrideFilter])

    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
    }

    const headCells: HeadCell<SchoolsTableRow>[] = [
        {
            disablePadding: false,
            align: 'center',
            id: 'schoolId',
            label: 'ID',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'recordName',
            label: 'Record name',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'effectiveName',
            label: 'Effective display name',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'placesName',
            label: 'Google Places name',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'overrideDisplay',
            label: 'EZOS override',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'outreachEligible',
            label: 'Outreach eligible',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'schoolRef',
            label: 'Actions',
            style: headCellStyle,
            sortable: false,
        },
    ]

    const tableRows = useMemo(
        () => filteredSchools.map((s) => createSchoolsTableRow(s)),
        [filteredSchools],
    )

    const sortedRows = useMemo(
        () => stableSort(tableRows, getComparator(order, orderBy)),
        [tableRows, order, orderBy],
    )

    const pagedRows = useMemo(
        () =>
            sortedRows.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage,
            ),
        [sortedRows, page, rowsPerPage],
    )

    const handleRequestSort = (
        _: React.MouseEvent<unknown>,
        property: keyof SchoolsTableRow,
    ) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    if (isLoadingAdminSchools) {
        return (
            <Container
                disableGutters
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
                    backgroundColor: theme.palette.grey[200],
                    maxWidth: 'none',
                    width: '100%',
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    return (
        <Container
            disableGutters
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.grey[200],
                minHeight: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
                height: '100%',
                overflow: 'hidden',
                maxWidth: 'none',
                width: '100%',
                margin: 0,
            }}
        >
            <Container
                disableGutters
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: theme.spacing(2),
                    overflow: 'hidden',
                    maxWidth: 'none',
                    width: '100%',
                    margin: 0,
                }}
            >
                <Paper
                    elevation={1}
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Container
                        disableGutters
                        style={{
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: theme.spacing(1.5),
                            padding: theme.spacing(2, 2, 1),
                            maxWidth: 'none',
                            width: '100%',
                            margin: 0,
                        }}
                    >
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search record name, Places name, EZOS override, or IPEDS id…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ width: 360, maxWidth: '100%' }}
                            inputProps={{
                                'aria-label':
                                    'Search schools by record name, Google Places name, EZOS override, or IPEDS id',
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            color="action"
                                            fontSize="small"
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Selector
                            label="Outreach eligible"
                            currentValue={eligibleFilter}
                            onChange={(e) =>
                                setEligibleFilter(Number(e.target.value))
                            }
                            data={YES_NO_FILTER}
                            getDisplayString={(item) => item.name}
                            noSelectionLabel="Any"
                            size="small"
                            variant="outlined"
                            customStyle={{
                                formControl: { minWidth: 190 },
                            }}
                        />
                        <Selector
                            label="Google Places name"
                            currentValue={placesFilter}
                            onChange={(e) =>
                                setPlacesFilter(Number(e.target.value))
                            }
                            data={PLACES_NAME_FILTER}
                            getDisplayString={(item) => item.name}
                            noSelectionLabel="Any"
                            size="small"
                            variant="outlined"
                            customStyle={{
                                formControl: { minWidth: 210 },
                            }}
                        />
                        <Selector
                            label="EZOS override"
                            currentValue={overrideFilter}
                            onChange={(e) =>
                                setOverrideFilter(Number(e.target.value))
                            }
                            data={OVERRIDE_FILTER}
                            getDisplayString={(item) => item.name}
                            noSelectionLabel="Any"
                            size="small"
                            variant="outlined"
                            customStyle={{
                                formControl: { minWidth: 170 },
                            }}
                        />
                        <IconButton
                            size="small"
                            aria-label="About the school catalog"
                            style={{ marginLeft: 'auto' }}
                            onClick={() => setHelpOpen(true)}
                        >
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Container>
                    <TableContainer
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            paddingLeft: theme.spacing(2),
                            paddingRight: theme.spacing(2),
                        }}
                    >
                        <Table
                            className={classes.table}
                            stickyHeader
                            aria-label="school catalog"
                            style={{ width: '100%' }}
                        >
                            <EnhancedTableHead<SchoolsTableRow>
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={tableRows.length}
                                headCells={headCells}
                            />
                            <TableBody>
                                {pagedRows.map((row) => (
                                    <TableRow key={row.schoolId.value}>
                                        <TableCell align="center">
                                            {row.schoolId.value}
                                        </TableCell>
                                        <TableCell>
                                            {row.recordName.value}
                                        </TableCell>
                                        <TableCell>
                                            {row.effectiveName.value}
                                        </TableCell>
                                        <TableCell>
                                            {row.placesName.value}
                                        </TableCell>
                                        <TableCell>
                                            {row.overrideDisplay.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.outreachEligible.value}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Edit EZOS override">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setDialogSchool(
                                                            row.schoolRef.value,
                                                        )
                                                    }
                                                    aria-label="edit ezos display override"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {pagedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={headCells.length}
                                            align="center"
                                        >
                                            No schools found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ProspectsTableFooter
                        count={sortedRows.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onChangePage={(_, newPage) => setPage(newPage)}
                        onChangeRowsPerPage={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10))
                            setPage(0)
                        }}
                    />
                </Paper>
            </Container>

            <Dialog
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="school-catalog-help-title"
            >
                <DialogTitle id="school-catalog-help-title">
                    School catalog
                </DialogTitle>
                <DialogContent>
                    {CATALOG_HELP_PARAGRAPHS.map((paragraph) => (
                        <Typography
                            key={paragraph}
                            variant="body1"
                            style={{
                                fontSize: '1.125rem',
                                lineHeight: 1.6,
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            {paragraph}
                        </Typography>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setHelpOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <SchoolEzosOverrideDialog
                open={dialogSchool !== null}
                school={dialogSchool}
                onClose={() => setDialogSchool(null)}
                onSave={async (override) => {
                    if (!dialogSchool) return
                    await updateAdminSchoolOverride.mutateAsync({
                        id: dialogSchool.id,
                        ezos_admin_display_name_override: override,
                    })
                }}
            />
        </Container>
    )
}
